import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that wraps the Screen Wake Lock API to prevent the device screen
 * from dimming/locking during long-running operations (e.g. AI generation).
 *
 * Fails silently on browsers that don't support the API — no errors, no broken UX.
 * Re-acquires the lock on visibilitychange because Safari releases it when the tab
 * becomes hidden.
 *
 * @param {boolean} isActive — when true, request a wake lock; when false, release it
 */
export const useWakeLock = (isActive) => {
  const wakeLockRef = useRef(null);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;

    try {
      // Release any existing lock before requesting a new one
      // to prevent stale sentinel references on rapid toggle
      if (wakeLockRef.current) {
        await wakeLockRef.current.release().catch(() => {});
      }
      wakeLockRef.current = await navigator.wakeLock.request('screen');
    } catch {
      // Request can fail if the document is not visible or the device
      // is low on battery — fail silently per project convention
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch {
        // Already released — ignore
      }
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isActive) {
      releaseWakeLock();
      return;
    }

    requestWakeLock();

    // Safari releases the wake lock when the tab becomes hidden —
    // re-acquire it when the page regains visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActive) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock();
    };
  }, [isActive, requestWakeLock, releaseWakeLock]);
};
